import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { User, Menu, Settings, Home, HelpCircle, CircleDollarSign, Link, ChevronDown, Download } from 'lucide-react';
import { Logo } from '../components/ui/Logo';
import { Dropdown } from '../components/ui/Dropdown';
import { getAccountDropdownItems } from '../utils/getAccountDropdownItems';
import { useUser } from '../hooks/useUser';
import { useLanguage } from '../hooks/useLanguage';
import { useWebSocket } from '../contexts/WebSocketContext';

/** Fallback, пока не загружен список из API. Имена должны совпадать с `bots.name` в SPA БД (не старые алиасы). */
const FALLBACK_SUBSCRIPTION_BOT_NAMES = [
  'kas',
  'sol',
  'bnb_usdt',
  'btc_usdc',
  'btc_usdt',
  'doge_usdt',
  'eth_usdt',
  'xrp_usdt',
] as const;

export const AdminPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { user } = useUser();
  const { currentLanguage, switchLanguage } = useLanguage();
  const { isConnected, connectionStatus } = useWebSocket();
  
  // Проверка PWA режима
  const isPWA = window.matchMedia('(display-mode: standalone)').matches || 
                (window.navigator as any).standalone === true;
  
  // Проверка Safe Area (только для iPhone)
  const hasSafeArea = window.matchMedia('(display-mode: standalone)').matches && 
                      getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top') !== '0px';
  const safeAreaTop = getComputedStyle(document.documentElement).getPropertyValue('--safe-area-top');
  const [users, setUsers] = React.useState<any[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [showUsers, setShowUsers] = React.useState(false);
  const [openUserIndex, setOpenUserIndex] = React.useState<number | null>(null);
  const [openBotIndex, setOpenBotIndex] = React.useState<number | null>(null);
  const [dateInputMode, setDateInputMode] = React.useState<'start' | 'end' | null>(null);
  const [manualDateInput, setManualDateInput] = React.useState('');
  const [showRevokeConfirm, setShowRevokeConfirm] = React.useState(false);
  const [showDeleteUserConfirm, setShowDeleteUserConfirm] = React.useState(false);
  const [showDeleteTradingHistoryConfirm, setShowDeleteTradingHistoryConfirm] = React.useState(false);
  const [showCancelSubscriptionConfirm, setShowCancelSubscriptionConfirm] = React.useState(false);
  const [currentUser, setCurrentUser] = React.useState<any>(null);
  const [currentCancelBot, setCurrentCancelBot] = React.useState<string>('');
  const [userApiKeys, setUserApiKeys] = React.useState<{ [userId: number]: { access_key: string | null; secret_key: string | null } }>({});
  const [userSubscriptions, setUserSubscriptions] = React.useState<{ [userId: number]: any[] }>({});
  const [currentBotName, setCurrentBotName] = React.useState<string>('');
  const [tempStartDate, setTempStartDate] = React.useState<string>('');
  const [tempEndDate, setTempEndDate] = React.useState<string>('');
  const [userFilter, setUserFilter] = React.useState<'all' | 'active' | 'expired' | 'new'>('all');

  // ===== Bots backups/controls state =====
  const [showBots, setShowBots] = React.useState(false);
  const [adminBots, setAdminBots] = React.useState<Array<{ id: number; name: string; display_name: string }>>([]);
  const [openBotAccordions, setOpenBotAccordions] = React.useState<Set<string>>(new Set());
  const [backupsByBot, setBackupsByBot] = React.useState<Record<string, { loading: boolean; backups: Array<{ filename: string; size: number; created_at: string }> }>>({});
  const [pagesByBot, setPagesByBot] = React.useState<Record<string, number>>({});
  const [applyBackupConfirm, setApplyBackupConfirm] = React.useState<{ open: boolean; botName?: string; filename?: string }>({ open: false });
  const [deleteBackupConfirm, setDeleteBackupConfirm] = React.useState<{ open: boolean; botName?: string; filename?: string }>({ open: false });
  const [stopBotConfirm, setStopBotConfirm] = React.useState<{ open: boolean; botName?: string }>({ open: false });
  const [startBotConfirm, setStartBotConfirm] = React.useState<{ open: boolean; botName?: string }>({ open: false });
  const [restartBotConfirm, setRestartBotConfirm] = React.useState<{ open: boolean; botName?: string }>({ open: false });
  const [botStatuses, setBotStatuses] = React.useState<Record<string, 'active' | 'inactive' | 'loading'>>({});

  // Проверка прав доступа к админ панели
  React.useEffect(() => {
    if (!user) {
      // Если пользователь не загружен, ждем
      return;
    }
    
    // Проверяем, является ли пользователь админом
    const checkAdminStatus = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/check_admin`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ user_id: user.user_id, email: user.email })
        });
        
        if (response.ok) {
          const data = await response.json();
          if (!data.is_admin) {
            console.warn('Unauthorized access attempt to admin panel');
            navigate('/', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking admin status:', error);
        navigate('/', { replace: true });
      }
    };
    
    checkAdminStatus();
  }, [user, navigate]);


  const accountItems = getAccountDropdownItems(t, navigate, user?.is_admin || false);

  const languageItems = [
    {
      id: 'eng',
      label: 'English',
      onClick: () => switchLanguage('ENG')
    },
    {
      id: 'rus',
      label: 'Русский',
      onClick: () => switchLanguage('RUS')
    }
  ];

  const pagesItems = [
    { id: 'home', label: t('home'), icon: Home, onClick: () => navigate('/') },
    { id: 'instructions', label: t('instructions'), icon: Settings, onClick: () => navigate('/instructions') },
    { id: 'faq', label: t('faq'), icon: HelpCircle, onClick: () => navigate('/faq') },
    { id: 'pricing', label: t('pricing'), icon: CircleDollarSign, onClick: () => navigate('/pricing') },
    { id: 'links', label: t('links'), icon: Link, onClick: () => navigate('/links') }
  ];

  // Функция для загрузки пользователей
  const fetchUsers = async () => {
    if (!user) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/users`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: parseInt(user.user_id),
          admin_email: user.email
        })
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data.users);
        
        // Загружаем подписки для всех пользователей параллельно
        const subscriptionPromises = data.users.map((user: any) => 
          fetchUserSubscriptions(user.user_id)
        );
        await Promise.all(subscriptionPromises);
      }
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Функция для загрузки API ключей пользователя
  const fetchUserApiKeys = async (userId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/get_user_api_keys`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        const data = await response.json();
        setUserApiKeys(prev => ({
          ...prev,
          [userId]: {
            access_key: data.access_key,
            secret_key: data.secret_key
          }
        }));
      }
    } catch (error) {
      console.error('Error fetching user API keys:', error);
    }
  };

  // Функция для загрузки подписок пользователя
  const fetchUserSubscriptions = async (userId: number) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/user/subscriptions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId })
      });
      if (response.ok) {
        const data = await response.json();
        setUserSubscriptions(prev => ({
          ...prev,
          [userId]: data.subscriptions || []
        }));
      }
    } catch (error) {
      console.error('Error fetching user subscriptions:', error);
    }
  };

  // Функция для показа/скрытия списка пользователей
  const toggleUsers = async () => {
    if (!showUsers) {
      await fetchUsers();
      // Тот же список ботов, что на вкладке «Боты» (display_name / name из БД)
      await fetchAdminBots();
    }
    setShowUsers(!showUsers);
    if (!showUsers) {
      setOpenUserIndex(null); // Закрываем все открытые пользователи при открытии списка
      setOpenBotIndex(null); // Закрываем все открытые боты
    }
  };

  // ===== Bots backups/controls logic =====
  const fetchAdminBots = async () => {
    if (!user) return;
    
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: parseInt(user.user_id),
          admin_email: user.email
        })
      });
      if (res.ok) {
        const data = await res.json();
        setAdminBots(data.bots || []);
        // После загрузки ботов проверяем их статус
        await checkBotsStatus();
      }
    } catch (e) {
      console.error('Error fetching bots:', e);
    }
  };

  /** Боты для блока подписок у пользователя: из API или fallback. */
  const botsForUserSubscriptions = React.useMemo(() => {
    if (adminBots.length > 0) {
      return adminBots;
    }
    return FALLBACK_SUBSCRIPTION_BOT_NAMES.map((name) => ({
      id: 0,
      name,
      display_name: '',
    }));
  }, [adminBots]);

  const checkBotsStatus = async () => {
    if (!user) return;
    
    try {
      // Устанавливаем статус "loading" для всех ботов
      const loadingStatuses: Record<string, 'loading'> = {};
      adminBots.forEach(bot => {
        loadingStatuses[bot.name] = 'loading';
      });
      setBotStatuses(loadingStatuses);

      const res = await fetch(`${process.env.REACT_APP_API_URL}/api/admin/check_bots_status`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: parseInt(user.user_id),
          admin_email: user.email
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        const statuses: Record<string, 'active' | 'inactive'> = {};
        
        // Преобразуем ответ в нужный формат
        Object.entries(data.bot_statuses || {}).forEach(([botName, statusData]: [string, any]) => {
          statuses[botName] = statusData.status === 'active' ? 'active' : 'inactive';
        });
        
        setBotStatuses(statuses);
      } else {
        // В случае ошибки устанавливаем все боты как неактивные
        const inactiveStatuses: Record<string, 'inactive'> = {};
        adminBots.forEach(bot => {
          inactiveStatuses[bot.name] = 'inactive';
        });
        setBotStatuses(inactiveStatuses);
      }
    } catch (e) {
      console.error('Error checking bots status:', e);
      // В случае ошибки устанавливаем все боты как неактивные
      const inactiveStatuses: Record<string, 'inactive'> = {};
      adminBots.forEach(bot => {
        inactiveStatuses[bot.name] = 'inactive';
      });
      setBotStatuses(inactiveStatuses);
    }
  };

  const toggleBots = async () => {
    if (!showBots) {
      await fetchAdminBots();
    }
    setShowBots(!showBots);
  };

  const loadBackupsFor = async (botName: string) => {
    if (!user) return;
    setBackupsByBot(prev => ({ ...prev, [botName]: { loading: true, backups: prev[botName]?.backups || [] } }));
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/backup/list`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName })
      });
      if (res.ok) {
        const data = await res.json();
        setBackupsByBot(prev => ({ ...prev, [botName]: { loading: false, backups: data.backups || [] } }));
        setPagesByBot(prev => ({ ...prev, [botName]: 1 }));
      } else {
        setBackupsByBot(prev => ({ ...prev, [botName]: { loading: false, backups: prev[botName]?.backups || [] } }));
      }
    } catch (e) {
      console.error('Error loading backups:', e);
      setBackupsByBot(prev => ({ ...prev, [botName]: { loading: false, backups: prev[botName]?.backups || [] } }));
    }
  };

  const handleCreateBackupFor = async (botName: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/backup/create`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName })
      });
      if (res.ok) {
        await loadBackupsFor(botName);
      }
    } catch (e) {
      console.error('Error creating backup:', e);
    }
  };

  const handleDownloadTradesCsv = async (botName: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/trades/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: user.user_id,
          admin_email: user.email,
          bot_name: botName,
        }),
      });
      if (!res.ok) {
        let msg = `HTTP ${res.status}`;
        try {
          const err = await res.json();
          if (err?.detail?.message) msg = err.detail.message;
          else if (typeof err?.detail === 'string') msg = err.detail;
        } catch {
          /* ignore */
        }
        alert(i18n.language === 'ENG' ? `Export failed: ${msg}` : `Ошибка выгрузки: ${msg}`);
        return;
      }
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition');
      let filename = `${botName}_trades.csv`;
      const m = cd?.match(/filename="([^"]+)"/);
      if (m) filename = m[1];
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      console.error('Error exporting trades CSV:', e);
      alert(i18n.language === 'ENG' ? 'Export failed' : 'Ошибка выгрузки');
    }
  };

  const handleApplyBackupFor = async (botName: string, filename: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/backup/apply`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName, filename })
      });
      if (res.ok) {
        setApplyBackupConfirm({ open: false });
      }
    } catch (e) {
      console.error('Error applying backup:', e);
    }
  };

  const handleDeleteBackupFor = async (botName: string, filename: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/backup/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName, filename })
      });
      if (res.ok) {
        setDeleteBackupConfirm({ open: false });
        await loadBackupsFor(botName);
      }
    } catch (e) {
      console.error('Error deleting backup:', e);
    }
  };

  const handleStopBotFor = async (botName: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/stop`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName })
      });
      if (res.ok) {
        setStopBotConfirm({ open: false });
      }
    } catch (e) {
      console.error('Error stopping bot:', e);
    }
  };

  const handleStartBotFor = async (botName: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName })
      });
      if (res.ok) {
        setStartBotConfirm({ open: false });
      } else {
        const txt = await res.text();
        console.error('Start bot failed:', res.status, txt);
      }
    } catch (e) {
      console.error('Error starting bot:', e);
    }
  };

  const handleRestartBotFor = async (botName: string) => {
    if (!user) return;
    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/admin/bots/restart`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ admin_user_id: user.user_id, admin_email: user.email, bot_name: botName })
      });
      if (res.ok) {
        setRestartBotConfirm({ open: false });
      } else {
        const txt = await res.text();
        console.error('Restart bot failed:', res.status, txt);
      }
    } catch (e) {
      console.error('Error restarting bot:', e);
    }
  };

  // Функция для получения текущей даты в формате YYYY-MM-DD
  const getCurrentDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Функция для получения отображаемого имени бота
  const getBotDisplayName = (botName: string) => {
    const displayNames: { [key: string]: string } = {
      'kas': 'KAS/USDT',
      'sol': 'SOL/USDT',
      'bnb_usdt': 'BNB/USDT',
      'btc_usdc': 'BTC/USDC',
      'btc_usdt': 'BTC/USDT',
      'doge_usdt': 'DOGE/USDT',
      'eth_usdt': 'ETH/USDT',
      'xrp_usdt': 'XRP/USDT',
    };
    return displayNames[botName] || botName;
  };

  // Функция для определения статуса подписок пользователя
  const getUserSubscriptionStatus = (userId: number): 'none' | 'active' | 'expired' => {
    const subscriptions = userSubscriptions[userId] || [];
    
    // Если подписок нет вообще
    if (subscriptions.length === 0) {
      return 'none';
    }
    
    // Проверяем есть ли активные подписки
    const hasActive = subscriptions.some(sub => {
      const endDate = new Date(sub.subscription_until);
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
      return endDate >= today;
    });
    
    return hasActive ? 'active' : 'expired';
  };

  const findSubscriptionForBot = (subscriptions: any[], botName: string) =>
    subscriptions.find((sub) => sub.name === botName);

  // Функция для определения статуса конкретного бота у пользователя
  const getUserBotStatus = (userId: number, botName: string): 'none' | 'active' | 'expired' => {
    const subscriptions = userSubscriptions[userId] || [];
    const botSubscription = findSubscriptionForBot(subscriptions, botName);
    
    // Если подписки на этого бота нет
    if (!botSubscription) {
      return 'none';
    }
    
    // Проверяем активна ли подписка на этого бота
    const endDate = new Date(botSubscription.subscription_until);
    const today = new Date();
    today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
    
    return endDate >= today ? 'active' : 'expired';
  };

  // Функция для получения отфильтрованного списка пользователей
  const getFilteredUsers = () => {
    return users.filter((user: any) => {
      const status = getUserSubscriptionStatus(user.user_id);
      switch (userFilter) {
        case 'active': return status === 'active';
        case 'expired': return status === 'expired';
        case 'new': return status === 'none';
        default: return true; // 'all'
      }
    });
  };

  // Функция для подсчета пользователей по категориям
  const getUserCounts = () => {
    const active = users.filter((user: any) => getUserSubscriptionStatus(user.user_id) === 'active').length;
    const expired = users.filter((user: any) => getUserSubscriptionStatus(user.user_id) === 'expired').length;
    const newUsers = users.filter((user: any) => getUserSubscriptionStatus(user.user_id) === 'none').length;
    return { active, expired, newUsers };
  };

  // Функция для обработки установки даты
  const handleSetDate = async (type: 'start' | 'end', date: string) => {
    if (!currentUser || !currentBotName || !user) {
      console.error('Missing required data for setting subscription date');
      return;
    }

    try {
      // Получаем текущую подписку для этого бота
      const currentSubscriptions = userSubscriptions[currentUser.user_id] || [];
      const currentSubscription = findSubscriptionForBot(currentSubscriptions, currentBotName);
      
      // Используем временные значения или текущие из базы данных
      const subscription_start = type === 'start' ? date : (tempStartDate || currentSubscription?.subscription_start);
      const subscription_until = type === 'end' ? date : (tempEndDate || currentSubscription?.subscription_until);

      // Для установки даты начала: если нет даты окончания, устанавливаем дату начала как дату окончания (временно)
      // Это позволит сохранить дату начала в базе, а дату окончания можно будет установить позже
      let finalSubscriptionStart = subscription_start;
      let finalSubscriptionUntil = subscription_until;

      if (type === 'start' && !subscription_until) {
        // Если устанавливаем дату начала, но нет даты окончания, используем дату начала как временную дату окончания
        finalSubscriptionUntil = date;
      } else if (type === 'end' && !subscription_start) {
        // Если устанавливаем дату окончания, но нет даты начала, используем текущую дату как дату начала
        finalSubscriptionStart = new Date().toISOString().split('T')[0];
      }

      const requestBody = {
        admin_user_id: String(user.user_id),
        admin_email: user.email,
        user_id: currentUser.user_id,
        bot_name: currentBotName,
        subscription_start: finalSubscriptionStart,
        subscription_until: finalSubscriptionUntil
      };

      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/set_subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (response.ok) {
        // Обновляем подписки пользователя
        await fetchUserSubscriptions(currentUser.user_id);
        // Очищаем временные значения
        setTempStartDate('');
        setTempEndDate('');
      } else {
        console.error('Error setting subscription date');
      }
    } catch (error) {
      console.error('Error setting subscription date:', error);
    } finally {
      setDateInputMode(null);
      setManualDateInput('');
    }
  };

  // Функция для продления подписки
  const handleExtendSubscription = async (days: number) => {
    if (!currentUser || !currentBotName || !user) {
      console.error('Missing required data for extending subscription');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/extend_subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: String(user.user_id),
          admin_email: user.email,
          user_id: currentUser.user_id,
          bot_name: currentBotName,
          days
        })
      });

      if (response.ok) {
        // Обновляем подписки пользователя
        await fetchUserSubscriptions(currentUser.user_id);
      } else {
        console.error('Error extending subscription');
      }
    } catch (error) {
      console.error('Error extending subscription:', error);
    }
  };

  // Функция для начала отмены подписки
  const handleCancelSubscription = (bot: string) => {
    if (!currentUser) {
      console.error('No user selected for subscription cancellation');
      return;
    }
    setCurrentCancelBot(bot);
    setShowCancelSubscriptionConfirm(true);
  };

  // Функция для подтверждения отмены подписки
  const confirmCancelSubscription = async () => {
    if (!currentUser || !currentCancelBot || !user) {
      console.error('Missing required data for subscription cancellation');
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/cancel_subscription`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          admin_user_id: String(user.user_id),
          admin_email: user.email,
          user_id: currentUser.user_id,
          bot_name: currentCancelBot
        })
      });

      if (response.ok) {
        // Обновляем подписки пользователя
        await fetchUserSubscriptions(currentUser.user_id);
        setShowCancelSubscriptionConfirm(false);
        setCurrentCancelBot('');
      } else {
        console.error('Error cancelling subscription');
      }
    } catch (error) {
      console.error('Error cancelling subscription:', error);
    }
  };

  // Функция для отзыва API ключей
  const handleRevokeAPI = () => {
    if (openUserIndex === null) {
      console.error('No user selected for API revocation');
      return;
    }
    const currentUser = users[openUserIndex];
    if (!currentUser) {
      console.error('No user selected for API revocation');
      return;
    }
    setCurrentUser(currentUser);
    setShowRevokeConfirm(true);
  };

  // Функция для подтверждения отзыва API ключей
  const confirmRevokeAPI = async () => {
    if (!currentUser) {
      console.error('No user selected for API revocation');
      return;
    }

    try {
      const res = await fetch(`${process.env.REACT_APP_API_URL}/api_keys/delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: currentUser.user_id })
      });
      
      if (!res.ok) {
        const data = await res.json();
        console.error('Error revoking API keys:', data);
        return;
      }

      // Обновляем состояние - очищаем API ключи пользователя
      setUserApiKeys(prev => ({
        ...prev,
        [currentUser.user_id]: {
          access_key: null,
          secret_key: null
        }
      }));


    } catch (error) {
      console.error('Error revoking API keys:', error);
    } finally {
      setShowRevokeConfirm(false);
    }
  };

  // Функция для отмены отзыва API ключей
  const cancelRevokeAPI = () => {
    setShowRevokeConfirm(false);
  };

  // Функция для показа модального окна удаления пользователя
  const handleDeleteUser = (user: any) => {
    setCurrentUser(user);
    setShowDeleteUserConfirm(true);
  };

  // Функция для подтверждения удаления пользователя
  const confirmDeleteUser = async () => {
    if (!currentUser || !user) {
      console.error('No user selected for deletion or admin not logged in');
      return;
    }

    try {
      const url = `${(process.env.REACT_APP_API_URL || '').replace('/api', '')}/admin/delete_user`;
      const requestBody = {
        admin_user_id: String(user.user_id),
        admin_email: user.email,
        user_id_to_delete: currentUser.user_id
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        const data = await res.json();
        console.error('Error deleting user:', data);
        return;
      }

      const responseData = await res.json();


      // Принудительно выходим удаленного пользователя из аккаунта
      if (responseData.deleted_user) {
        forceLogoutDeletedUser(responseData.deleted_user);
      }

      // Если удаляем текущего пользователя, выходим из аккаунта
      if (currentUser.user_id === user.user_id) {
  
        localStorage.removeItem('user');
        sessionStorage.clear();
        navigate('/');
        return;
      }

      // Обновляем список пользователей
      await fetchUsers();

    } catch (error) {
      console.error('Error deleting user:', error);
    } finally {
      setShowDeleteUserConfirm(false);
    }
  };

  // Функция для отмены удаления пользователя
  const cancelDeleteUser = () => {
    setShowDeleteUserConfirm(false);
  };

  // Функция для показа модального окна удаления истории торговли
  const handleDeleteTradingHistory = (user: any) => {
    setCurrentUser(user);
    setShowDeleteTradingHistoryConfirm(true);
  };

  // Функция для подтверждения удаления истории торговли
  const confirmDeleteTradingHistory = async () => {
    if (!currentUser || !user) {
      console.error('No user selected for trading history deletion or admin not logged in');
      return;
    }

    try {
      const url = `${(process.env.REACT_APP_API_URL || '').replace('/api', '')}/admin/delete_trading_history`;
      const requestBody = {
        admin_user_id: String(user.user_id),
        admin_email: user.email,
        user_id_to_delete: currentUser.user_id
      };

      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody)
      });

      if (!res.ok) {
        const data = await res.json();
        console.error('Error deleting trading history:', data);
        return;
      }


    } catch (error) {
      console.error('Error deleting trading history:', error);
    } finally {
      setShowDeleteTradingHistoryConfirm(false);
    }
  };

  // Функция для отмены удаления истории торговли
  const cancelDeleteTradingHistory = () => {
    setShowDeleteTradingHistoryConfirm(false);
  };

  // Функция для принудительного выхода удаленного пользователя
  const forceLogoutDeletedUser = (deletedUserData: any) => {
    // Проверяем, есть ли в localStorage данные удаленного пользователя
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        // Если это тот же пользователь, который был удален
        if (parsedUser.user_id === deletedUserData.user_id || parsedUser.email === deletedUserData.email) {
      
          localStorage.removeItem('user');
          sessionStorage.clear();
          // Перенаправляем на главную страницу
          window.location.href = '/';
        }
      } catch (error) {
        console.error('Error parsing stored user data:', error);
      }
    }
  };



  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="fixed top-0 left-0 w-full z-50 bg-header border-b border-border">
        <div className="flex items-center justify-between w-full h-20 px-4">
          {/* Левая колонка */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center space-x-4">
              <Logo color="primary" className="" style={{ color: '#525252', marginTop: '1mm', width: 'clamp(48px, 5.3vw, 64px)', height: 'clamp(48px, 5.3vw, 64px)' }} />
              <div className="flex flex-col justify-center min-w-0">
                <h1 className="text-3xl font-extra-bold neon-glow whitespace-nowrap" style={{ color: '#525252' }}>
                  ForsBot
                </h1>
                <p
                  className="text-textSubtle truncate"
                  style={{ fontSize: 'clamp(8px, 2vw, 16px)' }}
                >
                  {t('main_slogan')}
                </p>
              </div>
            </div>
          </div>
          {/* Правая колонка */}
          <div className="flex items-center space-x-1 flex-shrink-0">
            <Dropdown
              id="account"
              trigger={<User className="w-5 h-5 text-iconPrimary" />}
              items={accountItems}
              align="right"
            />
            <Dropdown
              id="language"
              trigger={<span className="text-sm font-medium text-iconPrimary">{currentLanguage}</span>}
              items={languageItems}
              align="right"
            />
            <Dropdown
              id="pages"
              trigger={<Menu className="w-5 h-5 text-iconPrimary" />}
              items={pagesItems}
              align="right"
            />
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="w-full px-4 sm:px-6 lg:px-8 py-8 max-w-6xl mx-auto" style={{ paddingTop: (isPWA && hasSafeArea) ? `calc(5rem + 2rem + ${safeAreaTop})` : 'calc(5rem + 2rem)' }}>
        <div className="text-center mb-8 md:mb-16">
          <h2 className="text-2xl md:text-3xl font-bold text-text mb-4 md:mb-8 font-sans flex items-center justify-center gap-2">
            <Settings className="w-7 h-7 text-primary" />
            {i18n.language === 'ENG' ? 'Admin Panel' : 'Админ панель'}
          </h2>
        </div>

        <div className="flex flex-col gap-4 w-full">
          {/* Аккордеон "Пользователи" */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${showUsers ? 'shadow-lg button-glow' : ''}`}
              onClick={toggleUsers}
              aria-expanded={showUsers}
            >
              <span className="text-left">{i18n.language === 'ENG' ? 'User Management' : 'Управление пользователями'}</span>
              <span className={`ml-2 transition-transform ${showUsers ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>
            
            {showUsers && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                {loading ? (
                  <div className="text-center text-textSubtle py-8">
                    {i18n.language === 'ENG' ? 'Loading users...' : 'Загрузка пользователей...'}
                  </div>
                ) : (
                  <div>
                    {/* Кнопки фильтров */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      {(() => {
                        const counts = getUserCounts();
                        return (
                          <>
                            <button
                              onClick={() => setUserFilter('all')}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors border border-border bg-card text-text hover:bg-hover focus:outline-none ${
                                userFilter === 'all' ? 'shadow-lg button-glow' : ''
                              }`}
                            >
                              {i18n.language === 'ENG' ? 'All' : 'Все'} ({users.length})
                            </button>
                            <button
                              onClick={() => setUserFilter('active')}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors border border-border bg-card text-text hover:bg-hover focus:outline-none ${
                                userFilter === 'active' ? 'shadow-lg button-glow' : ''
                              }`}
                            >
                              {i18n.language === 'ENG' ? 'Active' : 'Активные'} ✅ ({counts.active})
                            </button>
                            <button
                              onClick={() => setUserFilter('expired')}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors border border-border bg-card text-text hover:bg-hover focus:outline-none ${
                                userFilter === 'expired' ? 'shadow-lg button-glow' : ''
                              }`}
                            >
                              {i18n.language === 'ENG' ? 'Inactive' : 'Неактивные'} ❌ ({counts.expired})
                            </button>
                            <button
                              onClick={() => setUserFilter('new')}
                              className={`px-3 py-2 rounded-lg text-sm font-semibold transition-colors border border-border bg-card text-text hover:bg-hover focus:outline-none ${
                                userFilter === 'new' ? 'shadow-lg button-glow' : ''
                              }`}
                            >
                              {i18n.language === 'ENG' ? 'New' : 'Новые'} ({counts.newUsers})
                            </button>
                          </>
                        );
                      })()}
                    </div>
                    
                    {/* Список пользователей */}
                    <div className="flex flex-col gap-4">
                      {getFilteredUsers().map((user, index) => (
                      <div key={user.user_id} className="w-full">
                        <button
                          className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openUserIndex === index ? 'shadow-lg button-glow' : ''}`}
                          onClick={() => {
                            if (openUserIndex === index) {
                              setOpenUserIndex(null);
                              // Сбрасываем состояние аккордеонов при закрытии пользователя
                              setOpenBotIndex(null);
                              setDateInputMode(null);
                              setManualDateInput('');
                              setCurrentBotName('');
                              setCurrentUser(null);
                              setTempStartDate('');
                              setTempEndDate('');
                            } else {
                              setOpenUserIndex(index);
                              // Сбрасываем состояние аккордеонов при смене пользователя
                              setOpenBotIndex(null);
                              setDateInputMode(null);
                              setManualDateInput('');
                              setCurrentBotName('');
                              setCurrentUser(null);
                              // Загружаем API ключи при открытии пользователя (подписки уже загружены)
                              fetchUserApiKeys(user.user_id);
                            }
                          }}
                          aria-expanded={openUserIndex === index}
                        >
                          <div className="text-left">
                            <div className="font-semibold">
                              ID: {user.user_id}
                              {(() => {
                                const status = getUserSubscriptionStatus(user.user_id);
                                if (status === 'active') return <span className="ml-2">✅</span>;
                                if (status === 'expired') return <span className="ml-2">❌</span>;
                                return null;
                              })()}
                            </div>
                            <div className="text-sm text-textSubtle">{user.email}</div>
                          </div>
                          <span className={`ml-2 transition-transform ${openUserIndex === index ? 'rotate-180' : ''}`}>
                            <ChevronDown className="w-5 h-5" />
                          </span>
                        </button>
                        
                        {openUserIndex === index && (
                          <div className="mt-2 text-left text-sm bg-muted/30 rounded-lg p-4 animate-fade-in">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'User ID:' : 'ID пользователя:'}
                                </div>
                                <div className="text-sm text-textSubtle">
                                  {user.user_id}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'Email:' : 'Email:'}
                                </div>
                                <div className="text-sm text-textSubtle break-all">
                                  {user.email}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'Created:' : 'Создан:'}
                                </div>
                                <div className="text-sm text-textSubtle">
                                  {user.created_at}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'Active Subscriptions:' : 'Активные подписки:'}
                                </div>
                                <div className="text-sm text-textSubtle">
                                  {(() => {
                                    const subscriptions = userSubscriptions[user.user_id] || [];
                                    const activeSubscriptions = subscriptions.filter(sub => {
                                      const endDate = new Date(sub.subscription_until);
                                      const today = new Date();
                                      today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
                                      return endDate >= today;
                                    });
                                    
                                    if (activeSubscriptions.length > 0) {
                                      return `${activeSubscriptions.length} ${i18n.language === 'ENG' ? 'active subscriptions' : 'активных подписок'}`;
                                    } else {
                                      return i18n.language === 'ENG' ? 'No active subscriptions' : 'Нет активных подписок';
                                    }
                                  })()}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'Access Key:' : 'Access Key:'}
                                </div>
                                <div className="text-sm text-textSubtle break-all font-mono">
                                  {userApiKeys[user.user_id]?.access_key || (i18n.language === 'ENG' ? 'Not generated' : 'Не сгенерирован')}
                                </div>
                              </div>
                              <div>
                                <div className="font-semibold text-text mb-1">
                                  {i18n.language === 'ENG' ? 'Secret Key:' : 'Secret Key:'}
                                </div>
                                <div className="text-sm text-textSubtle break-all font-mono">
                                  {userApiKeys[user.user_id]?.secret_key || (i18n.language === 'ENG' ? 'Not generated' : 'Не сгенерирован')}
                                </div>
                              </div>
                            </div>
                            
                            {/* Кнопки управления пользователем */}
                            <div className="mb-4">
                              <div className="flex flex-col sm:flex-row gap-2">
                                <button
                                  onClick={handleRevokeAPI}
                                  className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {i18n.language === 'ENG' ? 'Revoke API Keys' : 'Отозвать API ключи'}
                                </button>
                                <button
                                  onClick={() => handleDeleteUser(user)}
                                  className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {i18n.language === 'ENG' ? 'Delete User' : 'Удалить пользователя'}
                                </button>
                                <button
                                  onClick={() => handleDeleteTradingHistory(user)}
                                  className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                  style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                >
                                  {i18n.language === 'ENG' ? 'Delete Trading History' : 'Удалить историю торговли'}
                                </button>
                              </div>
                            </div>
                            
                            {/* Управление ботами */}
                            <div className="border-t border-border pt-4">
                              <h4 className="font-semibold text-text mb-3">
                                {i18n.language === 'ENG' ? 'Bot Management' : 'Управление ботами'}
                              </h4>
                              <div className="flex flex-col gap-2">
                                {botsForUserSubscriptions.map((bot, botIndex) => (
                                  <div key={bot.name} className="w-full">
                                    <button
                                      className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border border-border bg-card text-text text-sm font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openBotIndex === botIndex ? 'shadow-lg button-glow' : ''}`}
                                      onClick={() => {
                                        if (openBotIndex === botIndex) {
                                          setOpenBotIndex(null);
                                          setCurrentBotName('');
                                          setCurrentUser(null);
                                          setTempStartDate('');
                                          setTempEndDate('');
                                        } else {
                                          setOpenBotIndex(botIndex);
                                          setCurrentBotName(bot.name);
                                          setCurrentUser(user);
                                          setTempStartDate('');
                                          setTempEndDate('');
                                        }
                                      }}
                                      aria-expanded={openBotIndex === botIndex}
                                    >
                                      <span className="text-left">
                                        {(bot.display_name && String(bot.display_name).trim()) || getBotDisplayName(bot.name)}
                                        {(() => {
                                          const status = getUserBotStatus(user.user_id, bot.name);
                                          if (status === 'active') return <span className="ml-2">✅</span>;
                                          if (status === 'expired') return <span className="ml-2">❌</span>;
                                          return null;
                                        })()}
                                      </span>
                                      <span className={`ml-2 transition-transform ${openBotIndex === botIndex ? 'rotate-180' : ''}`}>
                                        <ChevronDown className="w-4 h-4" />
                                      </span>
                                    </button>
                                    
                                                                         {openBotIndex === botIndex && (
                                       <div className="mt-2 text-left text-sm bg-muted/30 rounded-lg p-3 animate-fade-in border border-border">
                                         {/* Текущая подписка */}
                                         {(() => {
                                           const currentSubscriptions = userSubscriptions[user.user_id] || [];
                                           const currentSubscription = findSubscriptionForBot(currentSubscriptions, bot.name);
                                           
                                           if (currentSubscription) {
                                             // Проверяем, активна ли подписка
                                             const endDate = new Date(currentSubscription.subscription_until);
                                             const today = new Date();
                                             today.setHours(0, 0, 0, 0); // Сбрасываем время для корректного сравнения
                                             const isActive = endDate >= today;
                                             
                                             if (isActive) {
                                               // Вычисляем количество оставшихся дней
                                               const diffTime = endDate.getTime() - today.getTime();
                                               const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                               
                                               return (
                                                 <div className="mb-3 p-2 bg-card rounded border border-border">
                                                   <div className="font-semibold text-text mb-1">
                                                     {i18n.language === 'ENG' ? 'Active Subscription:' : 'Активная подписка:'}
                                                   </div>
                                                   <div className="text-xs text-textSubtle">
                                                     {i18n.language === 'ENG' ? 'From:' : 'С:'} {currentSubscription.subscription_start}
                                                   </div>
                                                   <div className="text-xs text-textSubtle">
                                                     {i18n.language === 'ENG' ? 'To:' : 'По:'} {currentSubscription.subscription_until}
                                                   </div>
                                                   <div className="text-xs text-green-500 font-semibold">
                                                     {i18n.language === 'ENG' ? `Active for ${diffDays} more days` : `Активна еще ${diffDays} дней`}
                                                   </div>
                                                 </div>
                                               );
                                             } else {
                                               return (
                                                 <div className="mb-3 p-2 bg-card rounded border border-border">
                                                   <div className="font-semibold text-text mb-1">
                                                     {i18n.language === 'ENG' ? 'Expired Subscription:' : 'Истекшая подписка:'}
                                                   </div>
                                                   <div className="text-xs text-textSubtle">
                                                     {i18n.language === 'ENG' ? 'From:' : 'С:'} {currentSubscription.subscription_start}
                                                   </div>
                                                   <div className="text-xs text-red-500">
                                                     {i18n.language === 'ENG' ? 'To:' : 'По:'} {currentSubscription.subscription_until}
                                                   </div>
                                                 </div>
                                               );
                                             }
                                           }
                                           
                                           return (
                                             <div className="mb-3 p-2 bg-card rounded border border-border">
                                               <div className="text-xs text-textSubtle">
                                                 {i18n.language === 'ENG' ? 'No active subscription' : 'Нет активной подписки'}
                                               </div>
                                             </div>
                                           );
                                         })()}
                                         
                                         {/* Управление датами подписки */}
                                         <div className="space-y-3">
                                          {/* Дата начала подписки */}
                                          <div>
                                            <div className="font-semibold text-text mb-2">
                                              {i18n.language === 'ENG' ? 'Subscription Start Date:' : 'Дата начала подписки:'}
                                            </div>
                                                                                         <div className="w-full">
                                               <button
                                                 className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border border-border bg-card text-text text-sm font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${dateInputMode === 'start' ? 'shadow-lg button-glow' : ''}`}
                                                 onClick={() => setDateInputMode(dateInputMode === 'start' ? null : 'start')}
                                                 aria-expanded={dateInputMode === 'start'}
                                               >
                                                 <span className="text-left">{i18n.language === 'ENG' ? 'Set Start Date' : 'Установить дату начала'}</span>
                                                 <span className={`ml-2 transition-transform ${dateInputMode === 'start' ? 'rotate-180' : ''}`}>
                                                   <ChevronDown className="w-4 h-4" />
                                                 </span>
                                               </button>
                                               
                                               {dateInputMode === 'start' && (
                                                 <div className="mt-2 space-y-2">
                                                   <div className="flex flex-col gap-2">
                                                     <button
                                                       onClick={() => setTempStartDate(getCurrentDate())}
                                                       className="w-full bg-card text-text px-3 py-2 rounded text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                       style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                                     >
                                                       {i18n.language === 'ENG' ? 'Current Date' : 'Текущая дата'}
                                                     </button>
                                                     <input
                                                       type="date"
                                                       value={tempStartDate}
                                                       onChange={(e) => setTempStartDate(e.target.value)}
                                                       className="w-full px-3 py-2 text-sm border border-border rounded bg-card text-text input-neon-focus"
                                                       placeholder={i18n.language === 'ENG' ? 'YYYY-MM-DD' : 'ГГГГ-ММ-ДД'}
                                                       style={{ 
                                                         fontSize: '16px',
                                                         height: '44px',
                                                         WebkitAppearance: 'none',
                                                         MozAppearance: 'none',
                                                         appearance: 'none'
                                                       }}
                                                     />
                                                     <button
                                                       onClick={() => handleSetDate('start', tempStartDate)}
                                                       disabled={!tempStartDate}
                                                       className="w-full bg-card text-text px-3 py-2 rounded text-sm font-semibold hover:bg-hover transition-colors disabled:opacity-50 border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                       style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                                     >
                                                       {i18n.language === 'ENG' ? 'Set' : 'Установить'}
                                                     </button>
                                                   </div>
                                                 </div>
                                               )}
                                             </div>
                                          </div>
                                          
                                          {/* Дата окончания подписки */}
                                          <div>
                                            <div className="font-semibold text-text mb-2">
                                              {i18n.language === 'ENG' ? 'Subscription End Date:' : 'Дата окончания подписки:'}
                                            </div>
                                                                                         <div className="w-full">
                                               <button
                                                 className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border border-border bg-card text-text text-sm font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${dateInputMode === 'end' ? 'shadow-lg button-glow' : ''}`}
                                                 onClick={() => setDateInputMode(dateInputMode === 'end' ? null : 'end')}
                                                 aria-expanded={dateInputMode === 'end'}
                                               >
                                                 <span className="text-left">{i18n.language === 'ENG' ? 'Set End Date' : 'Установить дату окончания'}</span>
                                                 <span className={`ml-2 transition-transform ${dateInputMode === 'end' ? 'rotate-180' : ''}`}>
                                                   <ChevronDown className="w-4 h-4" />
                                                 </span>
                                               </button>
                                               
                                               {dateInputMode === 'end' && (
                                                 <div className="mt-2 space-y-2">
                                                   <div className="flex flex-col gap-2">
                                                     <button
                                                       onClick={() => setTempEndDate(getCurrentDate())}
                                                       className="w-full bg-card text-text px-3 py-2 rounded text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                       style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                                     >
                                                       {i18n.language === 'ENG' ? 'Current Date' : 'Текущая дата'}
                                                     </button>
                                                     <input
                                                       type="date"
                                                       value={tempEndDate}
                                                       onChange={(e) => setTempEndDate(e.target.value)}
                                                       className="w-full px-3 py-2 text-sm border border-border rounded bg-card text-text input-neon-focus"
                                                       placeholder={i18n.language === 'ENG' ? 'YYYY-MM-DD' : 'ГГГГ-ММ-ДД'}
                                                       style={{ 
                                                         fontSize: '16px',
                                                         height: '44px',
                                                         WebkitAppearance: 'none',
                                                         MozAppearance: 'none',
                                                         appearance: 'none'
                                                       }}
                                                     />
                                                     <button
                                                       onClick={() => handleSetDate('end', tempEndDate)}
                                                       disabled={!tempEndDate}
                                                       className="w-full bg-card text-text px-3 py-2 rounded text-sm font-semibold hover:bg-hover transition-colors disabled:opacity-50 border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                       style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                                     >
                                                       {i18n.language === 'ENG' ? 'Set' : 'Установить'}
                                                     </button>
                                                   </div>
                                                 </div>
                                               )}
                                             </div>
                                          </div>
                                          
                                          {/* Отмена подписки */}
                                          <div className="mb-4">
                                            <button
                                              onClick={() => handleCancelSubscription(bot.name)}
                                              className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0 w-full"
                                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              {i18n.language === 'ENG' ? 'Cancel Subscription' : 'Отменить подписку'}
                                            </button>
                                          </div>
                                          
                                          {/* Продление подписки */}
                                          <div>
                                            <div className="font-semibold text-text mb-2">
                                              {i18n.language === 'ENG' ? 'Extend Subscription:' : 'Продлить подписку:'}
                                            </div>
                                                                                         <div className="grid grid-cols-2 gap-2">
                                               <button
                                                 onClick={() => handleExtendSubscription(30)}
                                                 className="bg-card text-text px-2 py-1 rounded text-xs font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                               >
                                                 +30 {i18n.language === 'ENG' ? 'days' : 'дней'}
                                               </button>
                                               <button
                                                 onClick={() => handleExtendSubscription(60)}
                                                 className="bg-card text-text px-2 py-1 rounded text-xs font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                               >
                                                 +60 {i18n.language === 'ENG' ? 'days' : 'дней'}
                                               </button>
                                               <button
                                                 onClick={() => handleExtendSubscription(180)}
                                                 className="bg-card text-text px-2 py-1 rounded text-xs font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                               >
                                                 +180 {i18n.language === 'ENG' ? 'days' : 'дней'}
                                               </button>
                                               <button
                                                 onClick={() => handleExtendSubscription(365)}
                                                 className="bg-card text-text px-2 py-1 rounded text-xs font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                               >
                                                 +365 {i18n.language === 'ENG' ? 'days' : 'дней'}
                                               </button>
                                             </div>
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Аккордеон "Боты" */}
          <div className="w-full">
            <button
              className={`flex items-center justify-between w-full px-4 py-3 rounded-lg border border-border bg-card text-text text-base md:text-lg font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${showBots ? 'shadow-lg button-glow' : ''}`}
              onClick={toggleBots}
              aria-expanded={showBots}
            >
              <span className="text-left">{i18n.language === 'ENG' ? 'Bots' : 'Боты'}</span>
              <span className={`ml-2 transition-transform ${showBots ? 'rotate-180' : ''}`}>
                <ChevronDown className="w-5 h-5" />
              </span>
            </button>

            {showBots && (
              <div className="mt-2 text-left text-sm md:text-base bg-muted/30 rounded-lg p-4 animate-fade-in">
                {/* WebSocket статус */}
                <div className="mb-4 p-3 bg-card border border-border rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="font-semibold text-text">
                      {i18n.language === 'ENG' ? 'WebSocket Status' : 'Статус WebSocket'}
                    </h4>
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-500' : connectionStatus === 'error' ? 'bg-red-500' : 'bg-yellow-500'}`} />
                      <span className="text-sm text-textSubtle">
                        {isConnected ? '8/8' : connectionStatus === 'error' ? 'Ошибка' : 'Подключение...'}
                      </span>
                    </div>
                  </div>
                  <div className="text-xs text-textSubtle mt-2">
                    {i18n.language === 'ENG' 
                      ? `Status: ${connectionStatus}` 
                      : `Статус: ${connectionStatus}`
                    }
                  </div>
                </div>
                
                <div className="flex flex-col gap-3">
                  {adminBots.map((b, idx) => (
                    <div key={b.id} className="w-full">
                      <button
                        className={`flex items-center justify-between w-full px-3 py-2 rounded-lg border border-border bg-card text-text text-sm font-semibold transition-colors duration-200 hover:bg-hover focus:outline-none ${openBotAccordions.has(b.name) ? 'shadow-lg button-glow' : ''}`}
                        onClick={() => {
                          const next = new Set(openBotAccordions);
                          if (next.has(b.name)) {
                            next.delete(b.name);
                          } else {
                            next.add(b.name);
                            if (!backupsByBot[b.name]) {
                              loadBackupsFor(b.name);
                            }
                          }
                          setOpenBotAccordions(next);
                        }}
                        aria-expanded={openBotAccordions.has(b.name)}
                      >
                        <div className="flex items-center gap-2">
                          <span className={`w-2 h-2 rounded-full ${
                            botStatuses[b.name] === 'active' ? 'bg-green-500' : 
                            botStatuses[b.name] === 'inactive' ? 'bg-red-500' : 
                            'bg-yellow-500 animate-pulse'
                          }`}></span>
                          <span className="text-left">{b.display_name || b.name}</span>
                        </div>
                        <span className={`ml-2 transition-transform ${openBotAccordions.has(b.name) ? 'rotate-180' : ''}`}>
                          <ChevronDown className="w-4 h-4" />
                        </span>
                      </button>

                      {openBotAccordions.has(b.name) && (
                        <div className="mt-2 text-left text-sm bg-muted/30 rounded-lg p-3 animate-fade-in border border-border">
                          <div className="flex flex-wrap gap-2 mb-3">
                            <button
                              onClick={() => loadBackupsFor(b.name)}
                              className="bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              {i18n.language === 'ENG' ? 'Refresh Backups' : 'Обновить список бэкапов'}
                            </button>
                            <button
                              onClick={() => handleCreateBackupFor(b.name)}
                              className="bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              {i18n.language === 'ENG' ? 'Create Backup' : 'Сделать бэкап'}
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDownloadTradesCsv(b.name)}
                              className="inline-flex items-center gap-1 bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Download className="w-4 h-4" />
                              {i18n.language === 'ENG' ? 'Download trades (CSV)' : 'Скачать сделки (CSV)'}
                            </button>
                            <button
                              onClick={() => setStartBotConfirm({ open: true, botName: b.name })}
                              className="bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              {i18n.language === 'ENG' ? 'Start Bot' : 'Запустить бота'}
                            </button>
                            {/* Перенос строки на мобильных после первых трёх кнопок */}
                            <div className="block sm:hidden w-full" />
                            <button
                              onClick={() => setRestartBotConfirm({ open: true, botName: b.name })}
                              className="bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus-visible:outline-none focus-visible:ring-0 active:outline-none active:ring-0 w-full sm:w-auto"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              {i18n.language === 'ENG' ? 'Restart Bot' : 'Перезагрузить бота'}
                            </button>
                            <button
                              onClick={() => setStopBotConfirm({ open: true, botName: b.name })}
                              className="bg-card text-text px-3 py-2 rounded-lg text-sm font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0 w-full sm:w-auto"
                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                            >
                              {i18n.language === 'ENG' ? 'Stop Bot' : 'Остановить бота'}
                            </button>
                          </div>

                          <div className="bg-card rounded-lg border border-border p-3">
                            {backupsByBot[b.name]?.loading ? (
                              <div className="text-center text-textSubtle py-6">{i18n.language === 'ENG' ? 'Loading...' : 'Загрузка...'}</div>
                            ) : (backupsByBot[b.name]?.backups || []).length === 0 ? (
                              <div className="text-center text-textSubtle py-6">{i18n.language === 'ENG' ? 'No backups' : 'Бэкапов нет'}</div>
                            ) : (
                              (() => {
                                const perPage = 5;
                                const page = pagesByBot[b.name] || 1;
                                const backupsAll = backupsByBot[b.name]?.backups || [];
                                const totalPages = Math.max(1, Math.ceil(backupsAll.length / perPage));
                                const start = (page - 1) * perPage;
                                const items = backupsAll.slice(start, start + perPage);
                                return (
                              <>
                              {/* Десктопная таблица */}
                              <div className="hidden md:block overflow-x-auto">
                                <table className="w-full text-left text-sm">
                                  <thead>
                                    <tr className="text-textSubtle">
                                      <th className="py-2 w-10">#</th>
                                      <th className="py-2">{i18n.language === 'ENG' ? 'Filename' : 'Файл'}</th>
                                      <th className="py-2">{i18n.language === 'ENG' ? 'Size' : 'Размер'}</th>
                                      <th className="py-2">{i18n.language === 'ENG' ? 'Date' : 'Дата'}</th>
                                      <th className="py-2 text-right">{i18n.language === 'ENG' ? 'Actions' : 'Действия'}</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {items.map((bk, i) => (
                                      <tr key={`${b.name}_${bk.filename}`} className="border-t border-border">
                                        <td className="py-2 text-textSubtle">{start + i + 1}</td>
                                        <td className="py-2 font-mono">{bk.filename}</td>
                                        <td className="py-2">{(bk.size / (1024 * 1024)).toFixed(2)} MB</td>
                                        <td className="py-2">{bk.created_at.replace('T', ' ').split('.')[0]}</td>
                                        <td className="py-2">
                                          <div className="flex gap-2 justify-end">
                                            <button
                                              onClick={() => setApplyBackupConfirm({ open: true, botName: b.name, filename: bk.filename })}
                                              className="bg-card text-text px-3 py-1 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              {i18n.language === 'ENG' ? 'Apply' : 'Применить'}
                                            </button>
                                            <button
                                              onClick={() => setDeleteBackupConfirm({ open: true, botName: b.name, filename: bk.filename })}
                                              className="bg-card text-text px-3 py-1 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                              style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                            >
                                              {i18n.language === 'ENG' ? 'Delete' : 'Удалить'}
                                            </button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>

                              {/* Мобильные карточки */}
                              <div className="md:hidden space-y-3">
                                {items.map((bk, i) => (
                                  <div key={`${b.name}_${bk.filename}_mobile`} className="bg-muted/20 border border-border rounded-lg p-3">
                                    <div className="flex items-center justify-between mb-2">
                                      <span className="text-textSubtle text-xs">#{start + i + 1}</span>
                                      <span className="text-textSubtle text-xs">{(bk.size / (1024 * 1024)).toFixed(2)} MB</span>
                                    </div>
                                    <div className="mb-2">
                                      <div className="font-mono text-sm text-text break-all">{bk.filename}</div>
                                      <div className="text-textSubtle text-xs mt-1">{bk.created_at.replace('T', ' ').split('.')[0]}</div>
                                    </div>
                                    <div className="flex gap-2">
                                      <button
                                        onClick={() => setApplyBackupConfirm({ open: true, botName: b.name, filename: bk.filename })}
                                        className="flex-1 bg-card text-text px-3 py-2 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        {i18n.language === 'ENG' ? 'Apply' : 'Применить'}
                                      </button>
                                      <button
                                        onClick={() => setDeleteBackupConfirm({ open: true, botName: b.name, filename: bk.filename })}
                                        className="flex-1 bg-card text-text px-3 py-2 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                        style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                      >
                                        {i18n.language === 'ENG' ? 'Delete' : 'Удалить'}
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                              <div className="flex items-center justify-between mt-3">
                                <span className="text-xs text-textSubtle">
                                  {i18n.language === 'ENG' ? 'Page' : 'Страница'} {page}/{totalPages}
                                </span>
                                <div className="flex gap-2">
                                  <button
                                    onClick={() => setPagesByBot(prev => ({ ...prev, [b.name]: Math.max(1, (prev[b.name] || 1) - 1) }))}
                                    disabled={page <= 1}
                                    className="bg-card text-text px-3 py-1 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border disabled:opacity-50 focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                    style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    {i18n.language === 'ENG' ? 'Prev' : 'Назад'}
                                  </button>
                                  <button
                                    onClick={() => setPagesByBot(prev => ({ ...prev, [b.name]: Math.min(totalPages, (prev[b.name] || 1) + 1) }))}
                                    disabled={page >= totalPages}
                                    className="bg-card text-text px-3 py-1 rounded-lg text-xs font-semibold hover:bg-hover active:bg-card transition-colors border border-border disabled:opacity-50 focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                                    style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
                                  >
                                    {i18n.language === 'ENG' ? 'Next' : 'Вперед'}
                                  </button>
                                </div>
                              </div>
                              </>
                                );
                              })()
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Подтверждение применения бэкапа */}
      {applyBackupConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Apply backup?' : 'Применить бэкап?'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' ? 'Hot-restore will update DB without stopping the bot.' : 'Горячее восстановление обновит БД без остановки бота.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setApplyBackupConfirm({ open: false })}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={() => applyBackupConfirm.botName && applyBackupConfirm.filename && handleApplyBackupFor(applyBackupConfirm.botName, applyBackupConfirm.filename)}
                className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение удаления бэкапа */}
      {deleteBackupConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Delete backup?' : 'Удалить бэкап?'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' ? 'This action cannot be undone.' : 'Это действие нельзя отменить.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setDeleteBackupConfirm({ open: false })}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={() => deleteBackupConfirm.botName && deleteBackupConfirm.filename && handleDeleteBackupFor(deleteBackupConfirm.botName, deleteBackupConfirm.filename)}
                className="bg-card text-text px-4 py-2 rounded-lg text-sm font-semibold hover:bg-hover transition-colors border border-border focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение остановки бота */}
      {stopBotConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Stop the bot?' : 'Остановить бота?'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' ? 'The service will be gracefully stopped.' : 'Сервис будет корректно остановлен.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStopBotConfirm({ open: false })}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={() => stopBotConfirm.botName && handleStopBotFor(stopBotConfirm.botName)}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение запуска бота */}
      {startBotConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Start the bot?' : 'Запустить бота?'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' ? 'The service will be started.' : 'Сервис будет запущен.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setStartBotConfirm({ open: false })}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={() => startBotConfirm.botName && handleStartBotFor(startBotConfirm.botName)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Подтверждение перезагрузки бота */}
      {restartBotConfirm.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Restart the bot?' : 'Перезагрузить бота?'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' ? 'The service will be restarted.' : 'Сервис будет перезапущен.'}
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setRestartBotConfirm({ open: false })}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={() => restartBotConfirm.botName && handleRestartBotFor(restartBotConfirm.botName)}
                className="bg-green-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-green-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения отзыва API */}
      {showRevokeConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Confirm API Key Revocation' : 'Подтверждение отзыва API ключей'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' 
                ? 'Are you sure you want to revoke the API keys? This action cannot be undone.' 
                : 'Вы уверены, что хотите отозвать API ключи? Это действие нельзя отменить.'
              }
            </p>
            <div className="flex gap-3 justify-end">
                             <button
                 onClick={cancelRevokeAPI}
                 className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
               >
                 {i18n.language === 'ENG' ? 'No' : 'Нет'}
               </button>
                             <button
                 onClick={confirmRevokeAPI}
                 className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                 style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
               >
                 {i18n.language === 'ENG' ? 'Yes' : 'Да'}
               </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения отмены подписки */}
      {showCancelSubscriptionConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Confirm Subscription Cancellation' : 'Подтверждение отмены подписки'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' 
                ? 'Are you sure you want to cancel subscription for this user? This action cannot be undone.' 
                : 'Вы уверены, что хотите отменить подписку у данного пользователя? Это действие нельзя отменить.'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setShowCancelSubscriptionConfirm(false)}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={confirmCancelSubscription}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления пользователя */}
      {showDeleteUserConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Confirm User Deletion' : 'Подтверждение удаления пользователя'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' 
                ? 'Are you sure you want to delete this user? This action cannot be undone and will remove all user data.' 
                : 'Вы уверены, что хотите удалить этого пользователя? Это действие нельзя отменить и удалит все данные пользователя.'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteUser}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={confirmDeleteUser}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно подтверждения удаления истории торговли */}
      {showDeleteTradingHistoryConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-card border border-border rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-text mb-4">
              {i18n.language === 'ENG' ? 'Confirm Trading History Deletion' : 'Подтверждение удаления истории торговли'}
            </h3>
            <p className="text-textSubtle mb-6">
              {i18n.language === 'ENG' 
                ? 'Are you sure you want to delete the trading history? This action cannot be undone.' 
                : 'Вы уверены, что хотите удалить историю торговли? Это действие нельзя отменить.'
              }
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={cancelDeleteTradingHistory}
                className="bg-border text-text px-4 py-2 rounded-lg text-sm font-semibold transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'No' : 'Нет'}
              </button>
              <button
                onClick={confirmDeleteTradingHistory}
                className="bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-semibold hover:bg-red-700 transition-colors focus:outline-none focus:ring-0 active:outline-none active:ring-0 focus-visible:outline-none focus-visible:ring-0"
                style={{ outline: 'none', WebkitTapHighlightColor: 'transparent' }}
              >
                {i18n.language === 'ENG' ? 'Yes' : 'Да'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 